import { useState } from "react";
import { getObjectFromSearchParams, isEmptyObject, getObjectWithoutEmptyValues } from "../helpers/methods";
import usePagination from "./usePagination";

/**
 * Alternative to usePaginationWithSearchParams that keeps search params in local state
 * (it does not sync with browser window location)
 *
 * @param filterPrefix - unused, for compatibility with usePaginationWithSearchParams
 * @param initialParams - initial value for search params
 * @param defaultPageSize - default page size - number of records per page
 * @param pageKey - URL search params key for the page number
 * @param pageSizeKey - URL search params key for the page size
 */
export const usePaginationWithLocalSearchParams = (
    filterPrefix = undefined,
    initialParams = new URLSearchParams(),
    defaultPageSize = 10,
    pageKey = "page",
    pageSizeKey = "page_size",
) => {
    const [searchParams, setSearchParams] = useState(initialParams);
    const [page, handleChangePage, resetPageNumber] = usePagination();

    const handleChangeSearchParams = (newParams) => {
        if (newParams === undefined){
            setSearchParams(objectToSearchParams({}));
        } else {
            const currentParams = getObjectFromSearchParams(searchParams);
            const updatedParams = { ...currentParams, ...newParams };
            setSearchParams(objectToSearchParams(getObjectWithoutEmptyValues(updatedParams)));
        }
    };

    const handleChangePageWithSearchParams = (e, pageNumber) => {
        handleChangePage(e, pageNumber);
        handleChangeSearchParams({ [pageKey]: pageNumber });
    };

    const [pageSize, setPageSize] = useState(defaultPageSize);

    const handleChangePageSizeWithSearchParams = (e, explicitNewPageSize) => {
        const newNumberFromEvent = Number(e.target.value);
        const newPageSize = newNumberFromEvent ? newNumberFromEvent : explicitNewPageSize;
        resetPageNumber();
        setPageSize(newPageSize);
        handleChangeSearchParams({
            [pageKey]: 1,
            [pageSizeKey]: newPageSize,
        });
    };

    return {
        page,
        pageSize,
        setPageSize,
        handleChangePageWithSearchParams,
        handleChangePageSizeWithSearchParams,
        resetPageNumber,
        searchParams,
        setSearchParams,
        handleChangeSearchParams,
    };
};

const objectToSearchParams = (obj) => {
    const params = new URLSearchParams();
    Object.keys(obj).forEach((key) => {
        if (obj[key] !== undefined && obj[key] !== null) {
            if (Array.isArray(obj[key])) {
                for (const arr_val of obj[key]) {
                    params.append(key, arr_val.toString())
                }
            }
            else {
                params.append(key, obj[key].toString());
            }
        }
    });
    return params;
};
